import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useDispatch } from 'src/store/useDispatch';
import { useSelector } from 'react-redux';
import { fetchPresaleOrders, updateField, updateSearch, resetSearch } from 'src/store/slice/presaleOrders';

import { Table } from 'src/components/TableNew';
import PageHeader from 'src/components/PageHeader';
import NoResult from 'src/components/NoResult/NoResult';

import { PRESALE_ORDERS_HEADERS } from 'src/utils/constants';
import PagePanel from 'src/components/PagePanel';
import { Container } from 'src/components/Container';
import { useSearchQueryEffect } from 'src/hooks/useSearchQueryEffect';
import request from 'src/request';
import { setAppAlert } from 'src/store/slice/appAlert';
import { AUTH_URL } from 'src/configs';
import { ReactComponent as EyeIcon } from 'src/assets/images/Eye.svg';
import OrderDetailsModal from './OrderDetailsModal';

const EyeButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.lightGrey};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;

    path {
      fill: ${({ theme }) => theme.dark};
      transition: fill 0.2s ease;
    }
  }

  &:hover svg path {
    fill: ${({ theme }) => theme.primary || '#007bff'};
  }
`;

const PresaleOrders = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [trxOperationLoading, setTrxOperationLoading] = useState(false);

  const dispatch = useDispatch();
  const {
    data: { presaleOrders, totalCount, totalPages, field, search },
    loading,
  } = useSelector(({ presaleOrders }) => presaleOrders);

  const runQuery = useCallback(
    (params: { page: number; field?: string; search: string }) => {
      dispatch(fetchPresaleOrders({ page: params.page, field: params.field, search: params.search }));
    },
    [dispatch],
  );

  const debouncedSearch = useSearchQueryEffect({
    search,
    field,
    currentPage,
    setCurrentPage,
    query: runQuery,
  });

  const confirmTransaction = () => {
    setTrxOperationLoading(true);

    request
      .put(`${AUTH_URL}/presale/admin/presale-transaction/confirm`, { transaction_id: selectedOrder._id })
      .then(() => {
        dispatch(
          setAppAlert({
            message: 'You have confirmed order',
            isSuccess: true,
          })
        );
        setTimeout(() => {
          dispatch(fetchPresaleOrders({ page: currentPage, field, search: debouncedSearch }));
        }, 100);
      })
      .catch(() => {
        dispatch(
          setAppAlert({
            message: 'Something went wrong',
            isSuccess: false,
          })
        );
      })
      .finally(() => {
        setSelectedOrder(null);
        setTrxOperationLoading(false);
      });
  };

  const cancelTransaction = () => {
    setTrxOperationLoading(true);

    request
      .put(`${AUTH_URL}/presale/admin/presale-transaction/reject`, {
        transaction_id: selectedOrder._id,
      })
      .then(() => {
        dispatch(
          setAppAlert({
            message: 'You have canceled order',
            isSuccess: true,
          })
        );
        setTimeout(() => {
          dispatch(fetchPresaleOrders({ page: currentPage, field, search: debouncedSearch }));
        }, 100);
      })
      .catch(() => {
        dispatch(
          setAppAlert({
            message: 'Something went wrong',
            isSuccess: false,
          })
        );
      })
      .finally(() => {
        setSelectedOrder(null);
        setTrxOperationLoading(false);
      });
  };

  useEffect(() => {
    return () => {
      dispatch(resetSearch());
    };
  }, [dispatch]);

  const items = presaleOrders ?? [];
  const tableLoading = loading || trxOperationLoading;

  return (
    <PagePanel>
      <PageHeader
        title={`Pre-sale Orders (${totalCount})`}
        entity="presaleOrders"
        updateEntityField={updateField}
        updateEntitySearch={updateSearch}
        dropdownTitle={PRESALE_ORDERS_HEADERS[0]}
        dropDownItems={['Order No', 'Status', 'Blockchain', 'BXT Base Price', 'Payment Coin']}
      />
      {items.length > 0 || tableLoading ? (
        <Table
          loading={tableLoading}
          headers={PRESALE_ORDERS_HEADERS}
          items={items}
          columns="1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr"
          detailsModalTitle="Order"
          fieldsToShow={[
            'order_number',
            'status',
            'blockchain',
            'bxlend_token_base_price',
            'payment_coin',
            'presale_stage',
            'token_allocation',
            'amount_in_usd',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          actions={(item) => {
            if (
              item.status === 'in_progress' ||
              item.status === 'approval pending' ||
              item.status === 'Approval pending' ||
              item.status === 'Approval Pending'
            ) {
              return (
                <Container display="flex" justifyContent="center" alignItems="center">
                  <EyeButton
                    onClick={(e) => {
                      e.stopPropagation();
                      const originalOrder = presaleOrders.find((order: any) => order._id === item._id);
                      setSelectedOrder(originalOrder);
                    }}
                    aria-label="View order details"
                  >
                    <EyeIcon />
                  </EyeButton>
                </Container>
              );
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}
      {selectedOrder && !trxOperationLoading && (
        <OrderDetailsModal
          order={selectedOrder}
          onConfirm={confirmTransaction}
          onCancel={cancelTransaction}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </PagePanel>
  );
};

export default PresaleOrders;
